import { Field, useFormikContext } from 'formik';
import { Grid, Stack, Button, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useState } from 'react';

import { AutoCompleteSelectUI, CheckboxUI, DatePickerUI, FormErrorUI, FormLabelUI, InputUI } from '../Interface';
import { MaterialTransportOperationList } from '../../config/materialTransport';
import companyService from '../../service/company.service';
import { sxConfig } from './materialTransport.style';

const MaterialTransportFields = ({ selectedValues, autocompleteOptions, findedOperation, operationColor }) => {
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const [companyAddress, setCompanyAddress] = useState(null);

  const getCompanyData = async (companyId) => {
    setCompanyAddress(null);

    if (!companyId) return;

    const selCom = await companyService.getCompanyById(companyId);
    const findedAddress = selCom.companyAdress.find((address) => address.active && address.default);

    setCompanyAddress(findedAddress);
  };

  const isCompanyChanged = values.companyId !== selectedValues?.companyId;

  const address = isCompanyChanged ? companyAddress : selectedValues?.company?.companyAdress?.[0];
  const formattedAddress = [address?.postCode, address?.city, address?.streetAddress].filter(Boolean).join(', ');

  return (
    <Grid container spacing={{ xs: 2, md: 1 }} mt={0.5}>
      <Grid item xs={12} md={4}>
        <Field
          type="autocomplete"
          name="companyId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Cég" />}
          variant="outlined"
          size="small"
          helperText={<FormErrorUI message={errors.companyId} />}
          onChange={(_e, newVal) => {
            getCompanyData(newVal?.value);
            setFieldValue('companyId', newVal?.value ?? null).then(() => {
              validateField('companyId');
            });
          }}
          InputLabelProps={{ shrink: values.companyId !== '' }}
          selectedValue={values.companyId}
          selectedLabelValue={selectedValues?.company?.name}
          dataset={autocompleteOptions.company}
        />

        {formattedAddress && (
          <Typography fontSize={13} color="text.secondary" mb={1}>
            {formattedAddress}
          </Typography>
        )}
      </Grid>

      <Grid item xs={12} md={4}>
        <Field
          type="autocomplete"
          name="projectId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Projekt" />}
          variant="outlined"
          size="small"
          helperText={<FormErrorUI message={errors.projectId} />}
          onChange={(_e, newVal) => {
            setFieldValue('projectId', newVal?.value ?? null).then(() => {
              validateField('projectId');
            });
          }}
          InputLabelProps={{ shrink: values.projectId !== '' }}
          selectedValue={values.projectId}
          selectedLabelValue={selectedValues?.project?.projectName}
          dataset={autocompleteOptions.project}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <Stack direction="row" spacing={1}>
          {MaterialTransportOperationList.map((operation) => {
            const isSelected = findedOperation?.value === operation.value;

            return (
              <Button
                key={operation.value}
                variant="outlined"
                onClick={() => setFieldValue('operation', operation.value)}
                startIcon={isSelected ? <CheckIcon /> : null}
                sx={sxConfig.operationButton(isSelected, operationColor)}
              >
                {operation.label}
              </Button>
            );
          })}
        </Stack>
      </Grid>
      <Grid item xs={12} md={4}>
        <Stack spacing={1} alignItems="flex-start">
          <Field
            type="date"
            name="plannedDate"
            component={DatePickerUI}
            value={values.plannedDate}
            fullWidth
            label={<FormLabelUI text="Tervezett dátum" />}
            variant="outlined"
            size="small"
            helperText={<FormErrorUI message={errors.plannedDate} />}
            onChange={(date) => {
              setFieldValue('plannedDate', date).then(() => {
                validateField('plannedDate');
              });
            }}
          />

          <Field
            name="completed"
            type="checkbox"
            as={CheckboxUI}
            checked={Boolean(values.completed)}
            label={<FormLabelUI text="Kész" />}
            sx={{
              ml: 0,
              '& .MuiCheckbox-root': {
                p: 1,
              },
              '& .MuiSvgIcon-root': {
                fontSize: 30,
              },
            }}
          />
        </Stack>
      </Grid>

      <Grid item xs={12} md={8}>
        <Field
          type="text"
          name="description"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Megjegyzés" />}
          variant="outlined"
          size="small"
          helperText={<FormErrorUI message={errors.description} />}
          InputLabelProps={{ shrink: values.description !== '' }}
          multiline
          rows={4}
        />
      </Grid>
    </Grid>
  );
};

export default MaterialTransportFields;
